import {
    ContextOptions,
    addDays,
    differenceInCalendarDays,
    differenceInSeconds,
    isBefore,
    max,
    startOfDay,
    startOfHour,
    subDays,
} from 'date-fns';
import { format, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { stubTrue } from 'lodash';

import { DateRange } from '@/store';

export const epochToDate = (inputDate: number | Date): Date => {
    if (inputDate instanceof Date) {
        return inputDate;
    }
    // determine if epoch date is in seconds or milliseconds
    // 99999999999 (11 digits) means Sat Mar 03 1973 09:46:39 UTC
    const isSeconds = inputDate < 99999999999;
    return isSeconds ? new Date(inputDate * 1000) : new Date(inputDate);
};

export const formatUTCEpochDate = (epochDate: number, options?: { formatToken?: string; includeUTC?: boolean }) => {
    const date = epochToDate(epochDate);

    // Convert the date to UTC (though it's already in UTC, this makes it explicit)
    const utcDate = toZonedTime(date, 'UTC');

    // Format the date as a UTC string
    const { formatToken = 'PPpp', includeUTC = true } = options || {};
    const token = includeUTC ? `${formatToken} 'UTC'` : formatToken;
    const formattedDate = format(utcDate, token);
    return formattedDate;
};

export const startOfDayUTC = (date: Date): Date => {
    const utcZoned = toZonedTime(date, 'UTC');
    const startOfTodayZoned = startOfDay(utcZoned);
    const start = fromZonedTime(startOfTodayZoned, 'UTC');
    return start;
};

export const startOfHourUTC = (date: Date): Date => {
    const utcZoned = toZonedTime(date, 'UTC');
    const startOfHourZoned = startOfHour(utcZoned);
    return fromZonedTime(startOfHourZoned, 'UTC');
};

export const trimToDateRange = <T extends { timestamp: string | number | Date }>(data: T[], range: DateRange): T[] => {
    const startTs = range.startDate.getTime();
    const endTs = range.endDate.getTime();
    return data.filter((d) => {
        const ts = new Date(d.timestamp).getTime();
        return startTs <= ts && ts <= endTs;
    });
};
/**
 * The whole days a date range covers, counting the day at each end.
 *
 * A range is held as two `yyyy-MM-dd` stamps and loaded from the start of the first day to the end of the
 * last, so a start and an end on the same day cover one day.
 */
export const spanInDays = (startDate: Date, endDate: Date, tz?: ContextOptions<Date>): number =>
    differenceInCalendarDays(endDate, startDate, tz) + 1;

/** The earliest start a range ending on `endDate` can have while covering no more than `maxDays` days. */
export const earliestStartForSpan = (endDate: Date, maxDays: number, tz?: ContextOptions<Date>): Date =>
    subDays(endDate, maxDays - 1, tz);

/** The latest end a range starting on `startDate` can have while covering no more than `maxDays` days. */
export const latestEndForSpan = (startDate: Date, maxDays: number, tz?: ContextOptions<Date>): Date =>
    addDays(startDate, maxDays - 1, tz);

/**
 * Brings a range covering more than `maxDays` back to that span by moving one edge.
 *
 * `keep` names the edge that stays put, which is the one the user just chose. A range already inside the
 * maximum is handed back as it is.
 */
export const clampRangeToMaxSpan = (
    { startDate, endDate }: DateRange,
    maxDays: number,
    keep: 'start' | 'end',
    tz?: ContextOptions<Date>
): DateRange => {
    if (spanInDays(startDate, endDate, tz) <= maxDays) {
        return { startDate, endDate };
    }
    return keep === 'start'
        ? { startDate, endDate: latestEndForSpan(startDate, maxDays, tz) }
        : { startDate: earliestStartForSpan(endDate, maxDays, tz), endDate };
};

export const isInRange = (
    value: Date,
    { startDate, endDate }: DateRange,
    {
        tz,
        boundType = 'closedOpen',
        resolution = 'ms',
    }: { tz: ContextOptions<Date>; boundType?: 'closed' | 'closedOpen'; resolution?: 'ms' | 'day' }
): boolean => {
    if (resolution === 'day') {
        value = startOfDay(value, tz);
        startDate = startOfDay(startDate, tz);
        endDate = startOfDay(endDate, tz);
    }

    if (isBefore(value, startDate)) {
        return false;
    }
    if (isBefore(value, endDate)) {
        return true;
    }
    if (value.getTime() === endDate.getTime()) {
        return boundType === 'closed' || startDate.getTime() === endDate.getTime();
    }
    return false;
};

export const consolidateIntervals = <D>({
    dataset,
    dateRangeForDatum,
    includeInterval = stubTrue,
}: {
    dataset: D[];
    dateRangeForDatum: (datum: D) => DateRange;
    includeInterval?: (datum: D) => boolean;
}) => {
    let currentInterval: DateRange | null = null;
    const observationIntervals: DateRange[] = [];

    dataset
        .filter(includeInterval)
        .map(dateRangeForDatum)
        .toSorted((a, b) => a.startDate.getTime() - b.startDate.getTime())
        .forEach(({ startDate, endDate }) => {
            // if we're at least a minute after the last observation, close off the current interval and start a new one with this datum
            // otherwise, extend the current interval to the end of this datum.
            if (!currentInterval || differenceInSeconds(startDate, currentInterval.endDate) > 60) {
                if (currentInterval) observationIntervals.push(currentInterval);
                currentInterval = { startDate, endDate };
            } else {
                currentInterval.endDate = max([endDate, currentInterval.endDate]);
            }
        });
    if (currentInterval) observationIntervals.push(currentInterval);
    return observationIntervals;
};
